import { observer } from 'mobx-react-lite'
import { type IColumn, Typography, Icon } from 'shared/ui'
import { DayjsFormats } from 'shared/lib'
import { EmptyCell } from 'shared/ui/Table'
import { uiStore } from 'shared/store/uiStore'
import { type AutomationLog } from 'entities/Integrations/model/AutomationLog'
import { AutomationLogExecutionStatus } from 'entities/Integrations/ui/AutomationLogExecutionStatus'
import { AutomationLogResultStatus } from 'entities/Integrations/ui/AutomationLogResultStatus'

import styles from './styles.module.scss'

export const getColumns = (): IColumn<AutomationLog>[] => {
  return [
    {
      field: 'source',
      name: 'Source',
      width: '100%',
      maxWidth: 200,
      renderRowCell: (row) => <SourceCell item={row} />,
      isDisabledChangeVisible: true,
    },
    {
      field: 'entity_id',
      name: 'Entity ID',
      tooltipQuestionProps: {
        placement: 'top',
        label: 'ID of the source record',
      },
      renderRowCell: (row) => <TextCell value={row.entity_id} />,
    },
    {
      field: 'callback_id',
      name: 'Callback ID',
      tooltipQuestionProps: {
        placement: 'top',
        label: 'Salesmsg ID for this specific automation event',
      },
      renderRowCell: (row) => <TextCell value={row.callback_id} />,
    },
    {
      field: 'step',
      name: 'Step',
      renderRowCell: (row) => <StepCell value={row.step} />,
    },
    {
      field: 'contact_name',
      name: 'Contact Name',
      renderRowCell: (row) => <TextCell value={row.contact_name || ''} />,
    },
    {
      field: 'contact_phone',
      name: 'Contact Phone',
      renderRowCell: (row) => <TextCell value={row.contact_phone || ''} />,
    },
    {
      field: 'url',
      name: 'URL',
      tooltipQuestionProps: {
        placement: 'top',
        label: (
          <>
            Salesmsg link that was triggered by HubSpot
            <br />
            workflow
          </>
        ),
      },
      renderRowCell: (row) => <TextCell value={row.url || ''} />,
    },
    {
      field: 'action',
      name: 'Action',
      renderRowCell: (row) => <TextCell value={row.action} />,
      isDisabledChangeVisible: true,
    },
    {
      field: 'status',
      name: 'Execution Status',
      isSortable: true,
      renderRowCell: (row) =>
        row.status ? <AutomationLogExecutionStatus status={row.status} /> : <EmptyCell />,
      isDisabledChangeVisible: true,
    },
    {
      field: 'result',
      name: 'Result',
      maxWidth: 184,
      renderRowCell: (row) => (
        <AutomationLogResultStatus
          status={row.status}
          failedReason={row.status === 'failed' ? row.failed_reason : null}
          resultTitle={row.status === 'success' && row.result?.title ? row.result.title : null}
        />
      ),
    },
    {
      field: 'executed_at',
      name: 'Executed at',
      isSortable: true,
      tooltipQuestionProps: {
        placement: 'top',
        width: 240,
        label: (
          <>
            When Salesmsg executed the
            <br />
            action triggered by your automation.
          </>
        ),
      },
      renderRowCell: (row) =>
        row.executed_at ? (
          uiStore.dayjs(row.executed_at).format(DayjsFormats.fullWithAtDash2)
        ) : (
          <EmptyCell />
        ),
    },
  ]
}

const SourceCell = observer(({ item }: { item: AutomationLog }) => (
  <div className={styles.sourceCell}>
    <div className={styles.icon}>{item.sourceIcon && <Icon icon={item.sourceIcon} />}</div>
    <Typography
      variant={'body-md-regular'}
      ellipsis
      tag='div'
      tooltipProps={{
        label: item.sourceTitle,
      }}
      ariaLabel={'SourceCell'}
    >
      {item.sourceTitle}
    </Typography>
  </div>
))

const TextCell = observer(({ value }: { value: string | number }) =>
  value ? (
    <Typography
      variant={'body-md-regular'}
      ellipsis
      tooltipProps={{
        label: value,
      }}
      tag='div'
      ariaLabel={'TextCell'}
    >
      {value}
    </Typography>
  ) : (
    <EmptyCell />
  )
)

const StepCell = observer(({ value }: { value: string }) => {
  const stepName = value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

  return value ? (
    <Typography
      variant={'body-md-regular'}
      ellipsis
      tooltipProps={{
        label: value,
      }}
      tag='div'
      ariaLabel={'TextCell'}
    >
      {stepName}
    </Typography>
  ) : (
    <EmptyCell />
  )
})
